import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EventProps } from 'react-big-calendar';
import { dayFillOffsets, DayFillEvent, DayFillPlan } from './dayFillPlan';

// --- Day fill: the gesture -----------------------------------------------------
// A block in the week view grows a grip on its right edge. Mouse down on it,
// drag across other day columns, let go: the block is repeated on every day
// between its own column and the one under the pointer (see dayFillPlan.ts for
// what "repeated" means). While dragging, a ghost of the block is drawn in
// each target column, red where the day is blocked.
//
// The grip stops the mousedown from reaching react-big-calendar, so neither a
// block move nor a slot selection starts; the rest of the gesture is tracked
// on document so the pointer may leave the calendar.

type Column = { left: number; width: number };

export type DayFillDragState = {
    event: DayFillEvent;
    columns: Column[];
    sourceIndex: number;
    targetIndex: number;
    /** Viewport box of the source block, clipped to the scrollable grid. */
    top: number;
    height: number;
};

const columnAt = (columns: Column[], x: number): number => {
    const index = columns.findIndex((column) => x >= column.left && x < column.left + column.width);
    if (index >= 0) return index;
    return x < columns[0].left ? 0 : columns.length - 1;
};

export function useDayFillDrag(onFill: (event: DayFillEvent, offsets: number[]) => void) {
    const [drag, setDrag] = useState<DayFillDragState | null>(null);
    const detachRef = useRef<(() => void) | null>(null);
    const onFillRef = useRef(onFill);
    onFillRef.current = onFill;

    const end = useCallback(() => {
        detachRef.current?.();
        detachRef.current = null;
        setDrag(null);
    }, []);

    const begin = useCallback((event: DayFillEvent, mouseEvent: React.MouseEvent<HTMLElement>) => {
        if (mouseEvent.button !== 0 || detachRef.current) return;
        const grip = mouseEvent.currentTarget;
        const sourceEl = grip.closest<HTMLElement>('.rbc-event');
        const grid = grip.closest<HTMLElement>('.rbc-time-content');
        if (!sourceEl || !grid) return;
        const columnEls = Array.from(grid.querySelectorAll<HTMLElement>('.rbc-day-slot'));
        const sourceIndex = columnEls.findIndex((column) => column.contains(sourceEl));
        if (sourceIndex < 0 || columnEls.length < 2) return;

        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();

        const columns = columnEls.map((column) => {
            const box = column.getBoundingClientRect();
            return { left: box.left, width: box.width };
        });
        const measure = (targetIndex: number): DayFillDragState => {
            const box = sourceEl.getBoundingClientRect();
            const clip = grid.getBoundingClientRect();
            const top = Math.max(box.top, clip.top);
            const bottom = Math.min(box.bottom, clip.bottom);
            return { event, columns, sourceIndex, targetIndex, top, height: Math.max(0, bottom - top) };
        };
        const onMove = (e: MouseEvent) => setDrag(measure(columnAt(columns, e.clientX)));
        const onUp = (e: MouseEvent) => {
            const targetIndex = columnAt(columns, e.clientX);
            end();
            const offsets = dayFillOffsets(sourceIndex, targetIndex);
            if (offsets.length) onFillRef.current(event, offsets);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') end();
        };

        const body = document.body;
        const previous = { userSelect: body.style.userSelect, cursor: body.style.cursor };
        body.style.userSelect = 'none';
        body.style.cursor = 'copy';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('keydown', onKey);
        detachRef.current = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('keydown', onKey);
            body.style.userSelect = previous.userSelect;
            body.style.cursor = previous.cursor;
        };
        setDrag(measure(sourceIndex));
    }, [end]);

    // Never leave document listeners behind if the calendar unmounts mid-drag.
    useEffect(() => end, [end]);

    return { drag, begin };
}

/**
 * Ghost of the dragged block in every target column: grey where the day
 * already has this block, red where a different block is in the way.
 */
export function DayFillOverlay({ drag, plan }: { drag: DayFillDragState | null; plan: DayFillPlan | null }) {
    if (!drag || !plan || !drag.height) return null;
    return createPortal(
        <>
            {plan.days.map((day) => {
                const column = drag.columns[drag.sourceIndex + day.offset];
                if (!column) return null;
                const blocked = day.event === null;
                const modifier = !blocked ? '' : day.alreadyThere ? ' scheduler-fill-ghost--already' : ' scheduler-fill-ghost--blocked';
                return (
                    <div
                        key={day.offset}
                        className={`scheduler-fill-ghost${modifier}`}
                        style={{ left: column.left, width: column.width, top: drag.top, height: drag.height }}
                    >
                        <span className="scheduler-fill-ghost-label">
                            {!blocked && drag.event.title}
                            {blocked && (day.alreadyThere ? 'Already here' : `Overlaps ${day.blockedBy}`)}
                        </span>
                    </div>
                );
            })}
        </>,
        document.body
    );
}

type DayFillEventContentProps = EventProps<any> & {
    showGrip: boolean;
    onFillStart: (event: DayFillEvent, mouseEvent: React.MouseEvent<HTMLElement>) => void;
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * Calendar event body: the title plus, in the series week view, a grip on
 * each side edge that starts a day fill. Either grip fills in either
 * direction; there are two only so dragging left has an obvious place to
 * grab. Clicks on a grip must not open the block's edit modal, so they are
 * swallowed here.
 */
export function DayFillEventContent({ title, event, showGrip, onFillStart }: DayFillEventContentProps) {
    const grip = (side: 'left' | 'right') => (
        <span
            className={`scheduler-fill-grip scheduler-fill-grip--${side}`}
            title={`Drag across the days to the ${side} to repeat this block on each of them`}
            onMouseDown={(e) => onFillStart(event, e)}
            onClick={stop}
            onDoubleClick={stop}
        >
            {side === 'left' ? '\u00ab' : '\u00bb'}
        </span>
    );
    if (!showGrip) return <>{title}</>;
    return (
        <>
            <span className="scheduler-fill-title">{title}</span>
            {grip('left')}
            {grip('right')}
        </>
    );
}
